import { css } from '@emotion/react';

import * as React from 'react';

import type { CmlContent } from '__generated__/graphql-types-do-not-use';

import { Grid, Typography, Typography2 } from '@coursera/cds-core';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import LearningObjective from 'bundles/unified-description-page-common/components/shared/LearningObjective';
import Subsection from 'bundles/unified-description-page-common/components/shared/Subsection';
import TogglableDescription from 'bundles/unified-description-page-common/components/shared/TogglableDescription';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import type { EnterpriseSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseSpecializationPageQuery';
import getSpecializationDescription from 'bundles/unified-description-page-common/utils/getSpecializationDescription';

import _t from 'i18n!nls/xdp';

type Props = {
  pageData?: Pick<
    EnterpriseSpecializationPageFieldsFragment | ConsumerSpecializationPageFieldsFragment,
    'cmlDescription' | 'projectsOverview' | 'description'
  >;
  learningObjectives?: CmlContent[];
  title?: string;
};

const styles = {
  listContainer: css`
    & > li {
      margin-bottom: var(--cds-spacing-100);
    }
  `,
  description: css`
    margin-bottom: var(--cds-spacing-200);
  `,
};

const LearningObjectives = ({ pageData, learningObjectives, title }: Props) => {
  const { enableXDPRedesignPhase1 } = useXDPRedesignPhase1Data();

  if (!learningObjectives?.length) {
    return null;
  }

  const getTitle = () => {
    if (title) {
      return title;
    }

    if (enableXDPRedesignPhase1) {
      return _t('Overview');
    }

    return _t("What you'll learn");
  };

  const getObjectives = () => {
    if (enableXDPRedesignPhase1) {
      return (
        <Grid item container spacing={8} component="ul" css={styles.listContainer}>
          {learningObjectives.map((objective) => {
            return (
              <Grid item xs={12} key={objective.cmlValue} component="li">
                <LearningObjective objective={objective} />
              </Grid>
            );
          })}
        </Grid>
      );
    }

    return (
      <Grid container spacing={16} component="ul">
        {learningObjectives.map((objective) => {
          return (
            <Grid item xs={12} key={objective.cmlValue} component="li">
              <LearningObjective objective={objective} />
            </Grid>
          );
        })}
      </Grid>
    );
  };

  if (enableXDPRedesignPhase1) {
    const descriptionWithProjectOverview = getSpecializationDescription(
      pageData?.cmlDescription,
      pageData?.projectsOverview
    );

    return (
      <Subsection
        title={
          <Typography2 component="h2" variant="subtitleLarge">
            {getTitle()}
          </Typography2>
        }
        spacing={18}
      >
        <TrackedDiv
          trackingName="what_you_will_learn_section"
          withVisibilityTracking={true}
          requireFullyVisible={false}
          trackClicks={false}
        >
          <Typography2 component="div" variant="bodyPrimary" css={styles.description}>
            <TogglableDescription
              description={pageData?.description}
              cmlDescription={descriptionWithProjectOverview}
              trackingName="about_description"
            />
          </Typography2>
          {getObjectives()}
        </TrackedDiv>
      </Subsection>
    );
  }

  return (
    <Subsection title={<Typography variant="h2semibold">{title ?? _t("What you'll learn")}</Typography>} spacing={24}>
      <TrackedDiv
        trackingName="what_you_will_learn_section"
        withVisibilityTracking={true}
        requireFullyVisible={false}
        trackClicks={false}
      >
        <Grid container spacing={16} component="ul">
          {learningObjectives.map((objective) => {
            return (
              <Grid item xs={12} sm={6} key={objective.cmlValue} component="li">
                <LearningObjective objective={objective} />
              </Grid>
            );
          })}
        </Grid>
      </TrackedDiv>
    </Subsection>
  );
};

export default LearningObjectives;
